import { z } from 'zod';
import { IsoDate } from '@yourstore/domain';
import type { ApiClient } from './api/client';

/**
 * `reporting-api`, as this client understands it.
 *
 * A **second** wire file, deliberately, and the reason is the same one that
 * keeps `wire.ts` from importing `core-api`'s: these are two services with two
 * agreements. `reporting-api` reads a replica, publishes its own window caps,
 * refuses where `core-api` clamps, and echoes the locale it answered in. Folding
 * its shapes into the staff wire would make one file that changes for two
 * unrelated reasons — and would hide that the analytics section can be entirely
 * absent (a deployment that has not routed the replica) while the rest of the
 * app is perfectly healthy.
 *
 * ## The index is the menu
 *
 * `GET /v1/reports` exists "precisely so clients discover the set", and this
 * client takes that literally: the section's tabs are built from whatever the
 * index returns, and the window control is bounded by the `max_window_days` the
 * index publishes rather than by a number copied out of the service's config.
 * A cap that moves on the server moves here on the next load. A cap copied into
 * a bundle would drift, and the first anyone would know of it is a `400`.
 */

/* ------------------------------------------------------------------ */
/* The index                                                           */
/* ------------------------------------------------------------------ */

/**
 * Either one cap, or a cap per grain (revenue's is 400 / 1 100 / 1 830 days,
 * because the cost is in the number of buckets drawn rather than in the span).
 */
export const MaxWindowDays = z.union([z.number(), z.record(z.string(), z.number())]);
export type MaxWindowDays = z.infer<typeof MaxWindowDays>;

export const ReportIndexEntry = z.object({
  id: z.string(),
  /** Absolute, `/v1`-prefixed, exactly as the service publishes it. */
  path: z.string(),
  summary: z.string(),
  max_window_days: MaxWindowDays,
});
export type ReportIndexEntry = z.infer<typeof ReportIndexEntry>;

export const ReportIndex = z.object({
  service: z.string(),
  tenant_id: z.string(),
  timezone: z.string(),
  reports: z.array(ReportIndexEntry),
});
export type ReportIndex = z.infer<typeof ReportIndex>;

/* ------------------------------------------------------------------ */
/* The window meta every report carries                                */
/* ------------------------------------------------------------------ */

const WindowMeta = {
  as_of: IsoDate,
  timezone: z.string(),
  date_from: IsoDate,
  date_to: IsoDate,
  /** True when `?days` exceeded the cap and the service narrowed it. */
  clamped: z.boolean(),
  max_window_days: z.number(),
};

/* ------------------------------------------------------------------ */
/* Revenue                                                             */
/* ------------------------------------------------------------------ */

const BySource = z.object({
  settlement_cents: z.number(),
  deposit_cents: z.number(),
  certificate_cents: z.number(),
  cash_credit_topup_cents: z.number(),
});
export type BySource = z.infer<typeof BySource>;

/**
 * The credit block, both provenances, beside the revenue and never inside it.
 *
 * The field names carry §7.4 rule 2 so a table's column headings cannot lose
 * it: `cash_top_up_cents` **is** income, `gift_issued_cents` and
 * `gift_spent_cents` never are, and `cash_spent_cents` is money that arrived on
 * an earlier date.
 */
const CreditFlow = z.object({
  cash_top_up_cents: z.number(),
  cash_spent_cents: z.number(),
  gift_issued_cents: z.number(),
  gift_spent_cents: z.number(),
});
export type CreditFlow = z.infer<typeof CreditFlow>;

const RevenueBucket = z.object({
  bucket: z.string(),
  total_cents: z.number(),
  by_source: BySource,
  discount_cents: z.number(),
  credit: CreditFlow,
  settlements: z.number(),
});

export const RevenueReport = z.object({
  ...WindowMeta,
  grain: z.enum(['day', 'week', 'month']),
  total_cents: z.number(),
  by_source: BySource,
  discount_cents: z.number(),
  credit: CreditFlow,
  settlements: z.number(),
  buckets: z.array(RevenueBucket),
});
export type RevenueReport = z.infer<typeof RevenueReport>;

/* ------------------------------------------------------------------ */
/* Bookings                                                            */
/* ------------------------------------------------------------------ */

const LeadTime = z.object({
  /** Published, not hidden: a median over four is not a median over four thousand. */
  sample: z.number(),
  median_hours: z.number().nullable(),
  p90_hours: z.number().nullable(),
});

const ByStatus = z.record(z.string(), z.number());

const BookingSourceRow = z.object({
  source: z.string(),
  bookings: z.number(),
  covers: z.number(),
  by_status: ByStatus,
  cancellations: z.number(),
  no_shows: z.number(),
  fee_charged_count: z.number(),
  fee_cents: z.number(),
  lead_time: LeadTime,
});

export const BookingsReport = z.object({
  ...WindowMeta,
  bookings: z.number(),
  covers: z.number(),
  by_status: ByStatus,
  cancellations: z.number(),
  no_shows: z.number(),
  fee_charged_count: z.number(),
  fee_cents: z.number(),
  lead_time: LeadTime,
  by_source: z.array(BookingSourceRow),
});
export type BookingsReport = z.infer<typeof BookingsReport>;

/* ------------------------------------------------------------------ */
/* Utilization                                                         */
/* ------------------------------------------------------------------ */

const UtilizationWeek = z.object({
  week: z.string(),
  date_from: IsoDate,
  date_to: IsoDate,
  open_minutes: z.number(),
  capacity_minutes: z.number(),
  occupied_minutes: z.number(),
  utilization: z.number().nullable(),
  bookings: z.number(),
  covers: z.number(),
});

export const UtilizationReport = z.object({
  ...WindowMeta,
  resources: z.array(
    z.object({
      resource_id: z.string(),
      name: z.string(),
      kind: z.string(),
      /** A pool reports **once**, with its lane count (spec §4.6). */
      units: z.number(),
      capacity: z.number(),
      active: z.boolean(),
      open_minutes: z.number(),
      capacity_minutes: z.number(),
      occupied_minutes: z.number(),
      utilization: z.number().nullable(),
      bookings: z.number(),
      covers: z.number(),
      weeks: z.array(UtilizationWeek),
    }),
  ),
});
export type UtilizationReport = z.infer<typeof UtilizationReport>;

/* ------------------------------------------------------------------ */
/* Services and guests                                                 */
/* ------------------------------------------------------------------ */

export const ServicesReport = z.object({
  ...WindowMeta,
  /** The language actually answered in, which is not always the one asked for. */
  locale: z.string().nullable(),
  items: z.array(
    z.object({
      item_id: z.string().nullable(),
      name: z.string(),
      kind: z.string().nullable(),
      lines: z.number(),
      qty: z.number(),
      list_cents: z.number(),
      discount_cents: z.number(),
      net_cents: z.number(),
      bookings: z.number(),
      covers: z.number(),
      average_party_size: z.number().nullable(),
    }),
  ),
});
export type ServicesReport = z.infer<typeof ServicesReport>;

export const GuestsReport = z.object({
  ...WindowMeta,
  guests: z.number(),
  new_guests: z.number(),
  returning_guests: z.number(),
  repeat_rate: z.number().nullable(),
  buckets: z.array(
    z.object({
      bucket: z.string(),
      new_guests: z.number(),
      returning_guests: z.number(),
      repeat_rate: z.number().nullable(),
    }),
  ),
  top_guests: z.array(
    z.object({
      client_id: z.string(),
      display_name: z.string(),
      lifetime_cents: z.number(),
      lifetime_discount_cents: z.number(),
      settlements: z.number(),
      visits: z.number(),
      first_seen_on: IsoDate.nullable(),
      last_seen_on: IsoDate.nullable(),
    }),
  ),
});
export type GuestsReport = z.infer<typeof GuestsReport>;

/* ------------------------------------------------------------------ */
/* Reading them                                                        */
/* ------------------------------------------------------------------ */

/**
 * A loaded report, **tagged by its id**.
 *
 * A union rather than a common base, because these five have almost nothing in
 * common past the window meta and pretending otherwise would push the
 * differences into the renderer as optional fields. Tagging at the fetch is what
 * lets the table switch narrow properly, with no cast anywhere.
 */
export type LoadedReport =
  | { id: 'reports.revenue'; body: RevenueReport }
  | { id: 'reports.bookings'; body: BookingsReport }
  | { id: 'reports.utilization'; body: UtilizationReport }
  | { id: 'reports.services'; body: ServicesReport }
  | { id: 'reports.guests'; body: GuestsReport };

export type ReportId = LoadedReport['id'];

const RENDERABLE: readonly ReportId[] = [
  'reports.revenue',
  'reports.bookings',
  'reports.utilization',
  'reports.services',
  'reports.guests',
];

/**
 * Whether this bundle has a table for a report the index published.
 *
 * The index is allowed to grow a sixth report before the app grows a table for
 * it — that is the whole point of publishing a set rather than hard-coding one —
 * so an unknown id is listed and labelled, never a crash and never silence.
 */
export function isRenderable(id: string): id is ReportId {
  return (RENDERABLE as readonly string[]).includes(id);
}

/** `/v1/reports/revenue` → `/reports/revenue`, which is what `ApiClient` wants. */
export function underV1(path: string): string {
  return path.startsWith('/v1') ? path.slice(3) : path;
}

export async function fetchIndex(api: ApiClient, signal: AbortSignal): Promise<ReportIndex> {
  return api.request({ method: 'GET', path: '/reports', schema: ReportIndex, signal });
}

export async function fetchReport(
  api: ApiClient,
  entry: ReportIndexEntry,
  query: Record<string, string | number | undefined>,
  signal: AbortSignal,
): Promise<LoadedReport> {
  const path = underV1(entry.path);
  const at = { method: 'GET', path, query, signal } as const;
  switch (entry.id) {
    case 'reports.revenue':
      return { id: entry.id, body: await api.request({ ...at, schema: RevenueReport }) };
    case 'reports.bookings':
      return { id: entry.id, body: await api.request({ ...at, schema: BookingsReport }) };
    case 'reports.utilization':
      return { id: entry.id, body: await api.request({ ...at, schema: UtilizationReport }) };
    case 'reports.services':
      return { id: entry.id, body: await api.request({ ...at, schema: ServicesReport }) };
    case 'reports.guests':
      return { id: entry.id, body: await api.request({ ...at, schema: GuestsReport }) };
    default:
      throw new Error(`This app has no table for "${entry.id}" yet.`);
  }
}

/**
 * The cap that applies to what is being asked for.
 *
 * Revenue publishes a cap *per grain*; everything else publishes one number.
 * The window control is clamped to whichever applies, so the UI cannot offer a
 * span the service will refuse — and it never has to know which report is which
 * to do it.
 */
export function capFor(entry: ReportIndexEntry, grain: string | undefined): number | null {
  const cap = entry.max_window_days;
  if (typeof cap === 'number') return cap === 0 ? null : cap;
  const byGrain = grain === undefined ? undefined : cap[grain];
  if (typeof byGrain === 'number') return byGrain;
  const values = Object.values(cap);
  return values.length === 0 ? null : Math.max(...values);
}
